import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { AlertCircle, Info } from 'lucide-react';
import { RHFCodes } from '../hook-form';
import FormProvider from '../hook-form/FormProvider';
import { SignInMethod, useLoginFlow } from '../../auth/LoginProvider';
import { Button } from '../ui/Button';
import { Alert, AlertDescription } from '../ui/Alert';
import { completeSignIn, getAfterSignInBrand } from '../../utils/owner-portal/completeSignIn';

interface ConfirmCodeProps {
  back(): void;
}

const defaultValues = {
  code1: '',
  code2: '',
  code3: '',
  code4: '',
  code5: '',
  code6: ''
};

const ConfirmCode = ({ back }: ConfirmCodeProps) => {
  const { emailToVerify, signInMethod, phoneToVerify, confirmationResult } = useLoginFlow();
  const [navigating, setNavigating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const methods = useForm({
    mode: 'onChange',
    defaultValues
  });

  const {
    watch,
    handleSubmit,
    formState: { isSubmitting, isValid }
  } = methods;

  const values = watch();

  useEffect(() => {
    const code = Object.values(values).join('');
    if (code.length === 6 && isValid) {
      // Code is complete and valid - could trigger auto-submit here if desired
    }
  }, [isValid, values]);

  const onSubmit = handleSubmit(async (data) => {
    setError(null);
    const code = Object.values(data).join('');

    if (signInMethod === SignInMethod.Email) {
      // Email verification is not used in Owner Portal - should not reach here
      console.warn('Email code verification not supported in Owner Portal');
      return;
    }

    if (signInMethod === SignInMethod.Phone) {
      if (!confirmationResult) {
        back();
        return;
      }
      try {
        // Post-sign-in recording (afterSignIn) runs inside completeSignIn.
        // Note: phone-only users without Firebase Auth email may fail afterSignIn server-side.
        await completeSignIn({
          brand: getAfterSignInBrand(),
          signInFn: () => confirmationResult.confirm(code)
        }).then(() => {
          // Keep the spinner active on successful sign in.
          setNavigating(true);
        });
      } catch (e: unknown) {
        const err = e as { code?: string };
        console.error('Phone confirmation failed:', e);
        if (err.code === 'auth/invalid-verification-code') {
          setError('The entered 6-digit code is invalid. Please try again.');
        } else if (err.code === 'auth/code-expired') {
          setError('The verification code has expired. Please request a new one.');
        } else {
          setError('Verification failed. Please try again.');
        }
      }
    }
  });

  if (!signInMethod || (signInMethod === SignInMethod.Phone && !confirmationResult)) {
    console.warn(
      'Missing/bad signInMethod or phone result',
      signInMethod,
      'confirmationResult',
      confirmationResult
    );
    back();
    return null;
  }

  const handleResendCode = async () => {
    // For phone authentication, user needs to go back and restart the process
    if (signInMethod === SignInMethod.Phone) {
      back();
    }
  };

  return (
    <FormProvider methods={methods} onSubmit={onSubmit}>
      <div className="flex flex-col gap-4">
        <Alert>
          <Info className="h-4 w-4" />
          <AlertDescription>
            We have sent a 6-digit confirmation code to{' '}
            {signInMethod === SignInMethod.Email ? emailToVerify : phoneToVerify}, please enter the
            code below to verify.
          </AlertDescription>
        </Alert>

        <RHFCodes keyName="code" inputs={['code1', 'code2', 'code3', 'code4', 'code5', 'code6']} />

        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <Button
          type="submit"
          className="mt-3 w-full"
          size="lg"
          loading={isSubmitting || navigating}
          disabled={!isValid}
        >
          Verify
        </Button>

        <p className="text-center text-sm">
          Don&apos;t have a code?{' '}
          <button
            type="button"
            onClick={handleResendCode}
            className="font-medium text-primary dark:text-secondary underline-offset-4 hover:underline"
          >
            Resend code
          </button>
        </p>
      </div>
    </FormProvider>
  );
};

export default ConfirmCode;
